import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { LabelledField } from '@/components/LabelledField';
import { ApiError } from '@/lib/api/client';
import { useCreateExport, useExportJob } from '../api';

function isoDay(offsetDays: number): string {
  const d = new Date(Date.now() + offsetDays * 24 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  wells: Array<{ id: string; name: string }>;
  defaultWellId?: string;
}

export function ExportDialog({ open, onOpenChange, wells, defaultWellId }: Props) {
  const [wellId, setWellId] = useState(defaultWellId ?? wells[0]?.id ?? '');
  const [from, setFrom] = useState(isoDay(-7));
  const [to, setTo] = useState(isoDay(0));
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  const create = useCreateExport();
  const job = useExportJob(jobId);

  const reset = () => {
    setJobId(null);
    setError(null);
  };
  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const submit = async () => {
    if (from > to) {
      setError('The start date must be on or before the end date.');
      return;
    }
    setError(null);
    try {
      // Whole-day range in UTC.
      const result = await create.mutateAsync({
        wellId,
        from: `${from}T00:00:00Z`,
        to: `${to}T23:59:59Z`,
      });
      setJobId(result.jobId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to start export');
    }
  };

  const status = job.data?.status;
  const inProgress =
    jobId !== null && (status === undefined || status === 'pending' || status === 'running');

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle>Export readings (CSV)</DialogTitle>
          <DialogDescription>
            Generate a CSV of a well&apos;s readings over a date range. Large ranges are prepared in
            the background.
          </DialogDescription>
        </DialogHeader>

        {jobId === null ? (
          <div className="space-y-4">
            <LabelledField label="Well" htmlFor="export-well">
              <Select value={wellId} onValueChange={setWellId}>
                <SelectTrigger id="export-well" data-testid="export-well">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {wells.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </LabelledField>
            <div className="grid grid-cols-2 gap-3">
              <LabelledField label="From" htmlFor="export-from">
                <Input
                  id="export-from"
                  type="date"
                  data-testid="export-from"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </LabelledField>
              <LabelledField label="To" htmlFor="export-to">
                <Input
                  id="export-to"
                  type="date"
                  data-testid="export-to"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </LabelledField>
            </div>
            {error && <div className="text-sm text-destructive">{error}</div>}
          </div>
        ) : (
          <div className="min-h-[80px] space-y-3" data-testid="export-progress">
            {inProgress && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Preparing your export…
              </div>
            )}
            {status === 'complete' && (
              <div className="space-y-2">
                <div className="text-sm text-foreground">Your export is ready.</div>
                <Button
                  data-testid="export-download"
                  onClick={() =>
                    job.data?.downloadUrl && window.open(job.data.downloadUrl, '_blank')
                  }
                >
                  <Download className="mr-1 h-4 w-4" /> Download CSV
                </Button>
              </div>
            )}
            {status === 'failed' && (
              <div className="space-y-2">
                <div className="text-sm text-destructive" data-testid="export-error">
                  {job.data?.error ?? 'Export failed.'}
                </div>
                <Button variant="outline" data-testid="export-retry" onClick={reset}>
                  Try again
                </Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {jobId === null ? (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button
                data-testid="export-submit"
                disabled={create.isPending || !wellId}
                onClick={() => void submit()}
              >
                {create.isPending ? 'Starting…' : 'Export'}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
